import { Link, useLocation, useNavigate } from "react-router-dom";

import { useAuthStore } from "src/store/authStore";
import { useLogout } from "src/hooks/useAuth";
import { usePortalMe } from "src/hooks/usePortal";
import { usePortalAuthStore } from "src/store/portalAuthStore";
import { useHoverDropdown } from "src/hooks/useHoverDropdown";
import { useDefaultDashboard } from "src/hooks/useDefaultDashboard";
import DashboardNavDropdown from "src/components/common/DashboardNavDropdown";
import { closeMobileNav } from "src/helpers/responsive";
import UserMenu from "src/components/common/UserMenu";
import DarkThemeToggle from "src/components/common/DarkThemeToggle";
import useDashboardStore from "src/store/dashboardStore";
import { SiteLogo } from "../../assets";

export default function AuthHeader() {
  // Which dropdown (by id) is currently open — a single key shared by every
  // dropdown in this header, driven by both hover and click/keyboard.
  const { openKey, open, scheduleClose, setOpenKey } = useHoverDropdown();

  const { pathname } = useLocation();

  const currentDashboardCodeFromPath = pathname.startsWith("/dashboards/")
    ? decodeURIComponent(pathname.replace("/dashboards/", ""))
    : "";

  const navigate = useNavigate();
  const { user: userData, refreshToken } = useAuthStore();
  const portalToken = usePortalAuthStore((s) => s.token);
  const { data: portalMe } = usePortalMe(!!portalToken);
  const isSuperAdmin = portalMe?.role === "super_admin";
  const { resetDashboardStore } = useDashboardStore();
  const { mutate: logout, isPending: isLoggingOut } = useLogout();

  const builderPath = currentDashboardCodeFromPath
    ? `/builder/${currentDashboardCodeFromPath}`
    : "/builder";

  const { allDashboards, setDefault } = useDefaultDashboard(!!userData);

  return (
      <nav className="navbar navbar-expand-lg py-2 py-lg-0 position-sticky top-0">
        <div className="container-fluid px-3 px-lg-5 gap-2 gap-lg-0">
          <Link className="navbar-brand text-decoration-none p-0" to={"/dashboards"}>
            <img src={SiteLogo} className="logo" alt="Analytics Elusa Health logo" />
          </Link>

          <div className="d-flex align-items-center gap-2 d-lg-none">
            <ul className="navbar-nav">
              <DarkThemeToggle />
            </ul>
            <button
                className="navbar-toggler"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#authHeader"
            >
              <span className="navbar-toggler-icon"></span>
            </button>
          </div>

          <div className="collapse navbar-collapse ms-0 ms-lg-3" id="authHeader">
            <ul className="navbar-nav me-auto">
              {!allDashboards ? (
                  <div className="placeholder-glow nav-item">
            <span
                className="placeholder"
                style={{ width: "160px", height: "30px" }}
            ></span>
                  </div>
              ) : (
                  <>
                    {allDashboards.default?.map((section) => {
                      const dropdownKey = `section-${section.code ?? section.type}`;
                      return (
                          <DashboardNavDropdown
                              key={dropdownKey}
                              dropdownKey={dropdownKey}
                              label={section.name}
                              dashboards={section.dashboards}
                              currentDashboardCode={currentDashboardCodeFromPath}
                              openKey={openKey}
                              onOpen={open}
                              onScheduleClose={scheduleClose}
                              onSetOpenKey={setOpenKey}
                              onNavigate={(code) => {
                                navigate(`/dashboards/${code}`);
                                closeMobileNav();
                              }}
                              onSetDefault={setDefault}
                              emptyMessage="Coming Soon..."
                          />
                      );
                    })}
                    {allDashboards.my_dashboards && (
                        <DashboardNavDropdown
                            dropdownKey="section-my-dashboards"
                            label="My Dashboards"
                            dashboards={allDashboards.my_dashboards}
                            currentDashboardCode={currentDashboardCodeFromPath}
                            openKey={openKey}
                            onOpen={open}
                            onScheduleClose={scheduleClose}
                            onSetOpenKey={setOpenKey}
                            onNavigate={(code) => {
                                navigate(`/dashboards/${code}`);
                                closeMobileNav();
                              }}
                            onSetDefault={setDefault}
                            emptyMessage="No dashboards found"
                        />
                    )}
                  </>
              )}
            </ul>
            <ul className="navbar-nav navbar-end">
                <DarkThemeToggle />
              {!userData ? (
                  <div className="placeholder-glow">
                      <span className="placeholder" style={{ width: "160px", height: "30px" }}></span>
                  </div>
              ) : (
                  <UserMenu
                      userData={userData}
                      isSuperAdmin={isSuperAdmin}
                      builderPath={builderPath}
                      onResetDashboardStore={resetDashboardStore}
                      openKey={openKey}
                      onOpen={open}
                      onScheduleClose={scheduleClose}
                      onSetOpenKey={setOpenKey}
                      onLogout={() => logout(refreshToken || "")}
                      isLoggingOut={isLoggingOut}
                  />
              )}
            </ul>
          </div>
        </div>
      </nav>
  );
}
